/* Donestate product cards: each card with several photos loops through them on its own
   while it is on screen (home page, collections, search, related products).
   On a computer, pointing at a card shows the next photo straight away and loops faster.
   Phones and computers set to reduce motion still loop, with a plain cut instead of a fade. */
(() => {
  const DELAY = 2400;       // time on each photo, in ms
  const HOVER_DELAY = 1100; // faster while the pointer is on the card
  const canHover = window.matchMedia('(hover: hover)').matches;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.dsLoop?.setVisible(entry.isIntersecting));
  }, { threshold: 0.5 });

  function setup(card) {
    if (card.dsLoop) return;
    const images = [...card.querySelectorAll('.showcase-card__image')];
    if (images.length < 2) return;
    const dots = [...card.querySelectorAll('.showcase-card__dot')];
    let index = 0;
    let timer = null;
    let visible = false;
    let hovering = false;
    let started = false;
    let loading = false;

    const ready = (img) => img.complete && img.naturalWidth > 0;
    const show = (next) => {
      images[index].classList.remove('is-active');
      dots[index]?.classList.remove('is-active');
      index = next;
      images[index].classList.add('is-active');
      dots[index]?.classList.add('is-active');
    };
    // Extra photos are rendered with data-src/data-srcset so they only download when they are about to show.
    const hydrate = (img) => {
      if (!img || !img.dataset.srcset) return;
      img.srcset = img.dataset.srcset; img.src = img.dataset.src;
      delete img.dataset.srcset; delete img.dataset.src;
    };
    // Track each photo's download state from the start, so a photo that loads or fails early is never missed.
    images.forEach((img) => {
      img.dsState = img.complete && img.naturalWidth > 0 ? 'ok' : img.complete && img.currentSrc ? 'error' : 'wait';
      img.addEventListener('load', () => { img.dsState = 'ok'; img.dsWake?.(); });
      img.addEventListener('error', () => { img.dsState = 'error'; img.dsBroken = true; img.dsWake?.(); });
    });
    // Resolves true once the photo has downloaded, false if it failed.
    const whenLoaded = (img) => {
      if (img.dsState === 'ok') return Promise.resolve(true);
      if (img.dsState === 'error') return Promise.resolve(false);
      return new Promise((resolve) => { img.dsWake = () => { img.dsWake = null; resolve(img.dsState === 'ok'); }; });
    };
    const advance = () => {
      if (document.hidden || loading) return;
      let next = (index + 1) % images.length;
      while (images[next].dsBroken && next !== index) next = (next + 1) % images.length;
      if (next === index) return;
      const img = images[next];
      hydrate(img);
      loading = true;
      whenLoaded(img).then((ok) => {
        loading = false;
        if (!ok) { img.dsBroken = true; return; }
        if (visible || hovering) { show(next); hydrate(images[(next + 1) % images.length]); }
      });
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = null;
      const playing = hovering || visible;
      if (!playing) return;
      // The first change on each card comes at a random moment, so the grid doesn't flip all at once.
      const wait = hovering ? HOVER_DELAY : started ? DELAY : DELAY * (0.4 + Math.random());
      started = true;
      timer = setTimeout(() => { advance(); schedule(); }, wait);
    };

    card.classList.add('is-looping');
    images[0].classList.add('is-active');
    card.dsLoop = {
      setVisible(value) {
        visible = value;
        // Warm up the next photo once the card is near the screen.
        if (value) hydrate(images[(index + 1) % images.length]);
        schedule();
      },
    };

    if (canHover) {
      card.addEventListener('mouseenter', () => { hovering = true; advance(); schedule(); });
      card.addEventListener('mouseleave', () => { hovering = false; schedule(); });
    }
    observer.observe(card);
  }

  const scan = (root) => root.querySelectorAll?.('.showcase-card--loop').forEach(setup);
  scan(document);
  // Cards added later (related products, sections reloaded in the theme editor).
  new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches('.showcase-card--loop')) setup(node);
      else scan(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
